import React, {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {
  Body,
  Button,
  Headline,
  Icon,
  Modal,
} from '@lightricks/react-design-system';
import useStoreFlowStartedEvent from '@contentlab/hooks/analytics/use-store-flow-started-event';
import useStoreViewDismissedEvent from '@contentlab/hooks/analytics/use-store-view-dismissed-event';
import useStoreViewPresentedEvent from '@contentlab/hooks/analytics/use-store-view-presented-event';
import useContactSales from '@contentlab/hooks/use-contact-sales';
import {
  getBundleTranslatedName,
  getPlanVariant,
  isFreePlan,
} from '@contentlab/views/contentlab/pricing/utils';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import Sentry from '@/services/sentry/Sentry';
import {
  CONTENT_STUDIO_PRICING_SOURCES,
  CONTENT_STUDIO_PRICING_VIEW_TYPES,
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import {removeContentLabWebPaymentFlowId} from '@/utils/LtidStorage';
import {raiseContentStudioError} from '@/utils/contentStudioErrorHandling';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import {ContentLabSubscription} from '@/types/models/pricing/contentLabSubscription';
import {ContactSalesButton} from '@/views/content-studio/pricing/components/pricing-bundle/Buttons';
import getActiveSubscription from '@/views/content-studio/utils/getActiveSubscription';
import ContentLabLogo from '@/components/ContentLabLogo';
import Link from '@/components/link';
import useContentStudioCancelSubscriptionMutation from '@/hooks/content-studio/mutations/useContentStudioCancelSubscriptionMutation';
import useCurrentSubscriptionQuery from '@/hooks/content-studio/use-current-subscription-query';
import useNavigation from '@/hooks/use-navigation';
import usePrevious from '@/hooks/use-previous';
import {ErrorKind} from '@/stores/contentStudioStore';
import styles from './ContentStudioPricingPage.module.scss';

function CancelSubscriptionModalContent({
  planName,
  cycleEndDate,
}: {
  planName: string;
  cycleEndDate: Date | null;
}) {
  const formattedCycleEndDate = cycleEndDate
    ? cycleEndDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';
  return (
    <div className={styles.modalContent}>
      <Headline size="md">
        {translate('cancel-subscription-title', {planName})}
      </Headline>
      <Body size="md">
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: translate('cancel-subscription-description', {
              type: planName,
              date: formattedCycleEndDate,
            }),
          }}
        />
      </Body>
    </div>
  );
}

function BottomSubscriptionInfo({
  activeSubscription,
  cancelSubscriptionHandler,
}: {
  activeSubscription?: ContentLabSubscription;
  cancelSubscriptionHandler: () => void;
}) {
  if (!activeSubscription || isFreePlan(activeSubscription)) return null;
  const isCancelledSubscription = !activeSubscription.isAutoRenewEnabled;
  if (isCancelledSubscription) {
    return (
      <div className={styles.bottomBar}>
        <Body size="md">
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: translate('full-access-until', {
                date: new Date(activeSubscription.renewAtMs).toLocaleDateString(
                  'en-US',
                  {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  }
                ),
              }),
            }}
          />
        </Body>
      </div>
    );
  }
  return (
    <div className={styles.bottomBar}>
      <Body size="md">{translate('Want to end your subscription?')}</Body>
      <Body
        size="md"
        className={styles.clickableText}
        onClick={cancelSubscriptionHandler}
      >
        {translate('Cancel now')}
      </Body>
    </div>
  );
}

const skippedFlowStartedEventSources = [
  CONTENT_STUDIO_PRICING_SOURCES.PRICING_MODAL,
];

function ContentStudioPricingPage() {
  const navigation = useNavigation();
  const location = useLocation();
  const previousLocation = usePrevious(location);
  const presentationIdRef = useRef(uuid());
  const {storeFlowStarted} = useStoreFlowStartedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {storeViewPresented} = useStoreViewPresentedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {storeViewDismissed} = useStoreViewDismissedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {data: currentSubscriptions} = useCurrentSubscriptionQuery();
  const activeSubscription = getActiveSubscription(
    currentSubscriptions?.subscriptions
  );
  const activePlanBundle = getPlanVariant(activeSubscription);
  const planName = getBundleTranslatedName(activePlanBundle);
  const {
    mutateAsync: cancelSubscription,
    isLoading: isCancelSubscriptionInProgress,
  } = useContentStudioCancelSubscriptionMutation();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const onContactSalesClick = useContactSales({
    flowName: 'pricing-page-contact-sales',
    interactionLocation: 'pricing-page',
  });

  useEffect(() => {
    removeContentLabWebPaymentFlowId();
    const flow = AnalyticsService.getOrCreateFlow(
      FLOW_NAMES.CONTENT_STUDIO.PRICING
    );
    if (!flow.source || !skippedFlowStartedEventSources.includes(flow.source)) {
      storeFlowStarted({
        flow_id: flow.flow_id,
        product_type: 'subscription',
        source: flow.source || '',
      });
    }

    storeViewPresented({
      presentation_id: presentationIdRef.current,
      view_name: CONTENT_STUDIO_VIEW_NAMES.PRICING,
      view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_PAGE,
    });

    return () => {
      storeViewDismissed({
        presentation_id: presentationIdRef.current,
        view_name: CONTENT_STUDIO_VIEW_NAMES.PRICING,
        view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_PAGE,
      });
    };
  }, []);

  const handleBackClick = () => {
    const previousPath = previousLocation?.pathname;
    if (
      previousPath?.startsWith(ROUTES.CONTENT_LAB) &&
      previousPath !== ROUTES.CONTENT_LAB_PRICING
    ) {
      navigation.goBack();
    } else {
      navigation.navigate(ROUTES.CONTENT_LAB);
    }
  };

  const handleCancelSubscription = async () => {
    const showErrorToast = () => {
      raiseContentStudioError(
        'Failed to cancel subscription, please contact support',
        ErrorKind.FAILURE
      );
    };
    try {
      if (!activeSubscription?.id) {
        showErrorToast();
        return;
      }

      await cancelSubscription(activeSubscription.id);

      raiseFlashMessage({
        status: 'success',
        mode: 'light',
        message: translate('Subscription cancelled successfully'),
      });
    } catch (error) {
      showErrorToast();
      if (error instanceof Error) {
        Sentry.captureException(error, {
          title: 'Error cancelling subscription',
          activeSubscription,
        });
      }
    } finally {
      setIsCancelModalOpen(false);
    }
  };

  const subscriptionCycleEndDate = activeSubscription?.renewAtMs
    ? new Date(activeSubscription.renewAtMs)
    : null;

  return (
    <div
      className={styles.pricingPage}
      data-testid="content-studio-pricing-page"
    >
      <div className={styles.topBar}>
        <Button
          appearance="neutral"
          mode="plain"
          size="medium"
          className={styles.backButton}
          icon={
            <Icon
              size="medium"
              appearance="neutral"
              name="Actions-Arrow-Back-Small"
            />
          }
          onClick={handleBackClick}
        >
          {translate('Back')}
        </Button>
        <Link to={ROUTES.CONTENT_LAB} className={styles.CSLogo}>
          <ContentLabLogo />
        </Link>
      </div>
      <Headline size="lg" className={styles.title}>
        {translate('Find the plan that’s right for you')}
      </Headline>
      <Body size="md" className={styles.salesBody}>
        {translate(
          'Contact sales to buy or upgrade LTX Scaler. Your current subscription keeps working.'
        )}
      </Body>
      <div className={styles.salesCta}>
        <ContactSalesButton onContactSalesClick={onContactSalesClick} />
      </div>
      <BottomSubscriptionInfo
        activeSubscription={activeSubscription}
        cancelSubscriptionHandler={() => setIsCancelModalOpen(true)}
      />
      <Modal
        open={isCancelModalOpen}
        showCloseButton
        handleClose={() => setIsCancelModalOpen(false)}
        handleSecondaryButtonClick={() => setIsCancelModalOpen(false)}
        primaryButtonText={translate(
          'cancel-subscription-modal-primary-button-text',
          {
            planName,
          }
        )}
        primaryButtonProps={{
          className: styles.cancelSubscriptionButton,
          disabled: isCancelSubscriptionInProgress,
        }}
        handlePrimaryButtonClick={handleCancelSubscription}
        secondaryButtonText={translate('Go back')}
        size="large"
      >
        <CancelSubscriptionModalContent
          planName={planName}
          cycleEndDate={subscriptionCycleEndDate}
        />
      </Modal>
    </div>
  );
}

export default ContentStudioPricingPage;
